import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, Phone, HeartPulse, ChevronRight } from 'lucide-react';

const Navbar = ({ lang = 'en', toggleLang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isAr = lang === 'ar';

  const navLinks = [
    { name: 'Home', nameAr: 'الرئيسية', path: '/' },
    { name: 'Services', nameAr: 'خدماتنا', path: '/services' },
    { name: 'Gallery', nameAr: 'المعرض', path: '/gallery' },
    { name: 'Pricing', nameAr: 'الأسعار', path: '/pricing' },
    { name: 'Contact', nameAr: 'اتصل بنا', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLanguageChange = () => {
    if (typeof toggleLang === 'function') {
      toggleLang();
      setIsOpen(false);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-black/90 backdrop-blur-xl border-b border-white/10 py-3'
            : 'bg-[#050505] border-b border-white/5 py-5'
        }`}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className='section-padding max-w-7xl mx-auto'>
          <div className='flex items-center justify-between'>
            {/* Logo Section */}
            <Link to='/' className='flex items-center gap-3 group'>
              <div className='relative p-1 transition-transform group-hover:scale-110'>
                {/* Wrapper for circular shape */}
                <div className='h-12 w-12 rounded-full overflow-hidden border-2 border-medical-blue-400 bg-white/10 flex items-center justify-center'>
                  <img
                    src='/images/logo.png'
                    alt='Right Choice Logo'
                    className='h-full w-full object-cover'
                  />
                </div>
              </div>
              <div className='flex flex-col'>
                <span className='text-xl md:text-2xl font-black tracking-tight text-white'>
                  RIGHT CHOICE
                </span>
                <span className='text-[10px] font-bold uppercase tracking-[0.2em] text-medical-blue-400'>
                  {isAr ? 'للرعاية الصحية' : 'Health Care'}
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className='hidden md:flex items-center gap-8'>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative text-sm font-bold transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'text-medical-blue-400'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {isAr ? link.nameAr : link.name}
                  {location.pathname === link.path && (
                    <motion.div
                      layoutId='navUnderline'
                      className='absolute -bottom-1 left-0 right-0 h-0.5 bg-medical-blue-500 rounded-full'
                    />
                  )}
                </Link>
              ))}

              {/* Language Switcher */}
              <button
                onClick={handleLanguageChange}
                className='flex items-center gap-2 px-4 py-2 border border-white/10 rounded-full text-xs font-bold text-gray-300 hover:bg-white/5 transition-all'
              >
                <Globe className='h-4 w-4 text-medical-blue-400' />
                {isAr ? 'English' : 'عربي'}
              </button>

              {/* Call Action Button */}
              <a
                href='tel:+96550097296'
                className='p-3 bg-medical-blue-600 text-white rounded-full shadow-lg hover:bg-medical-blue-500 hover:scale-110 active:scale-95 transition-all'
              >
                <Phone className='h-5 w-5' />
              </a>
            </nav>

            {/* Mobile Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className='md:hidden p-2 rounded-xl bg-white/5 text-white border border-white/10'
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className='fixed inset-0 z-[60] md:hidden bg-black'
            dir={isAr ? 'rtl' : 'ltr'}
          >
            <div className='flex flex-col h-full p-8'>
              <div className='flex items-center justify-between mb-12'>
                <div className='flex items-center gap-2'>
                  <HeartPulse className='text-medical-blue-500 h-6 w-6' />
                  <span className='text-xl font-black text-white'>
                    RIGHT CHOICE
                  </span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className='p-2 rounded-full bg-white/5 text-white border border-white/10'
                >
                  <X size={24} />
                </button>
              </div>

              <nav className='flex-1 space-y-4'>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center justify-between p-5 rounded-2xl font-bold transition-all ${
                      location.pathname === link.path
                        ? 'bg-medical-blue-600 text-white shadow-xl shadow-medical-blue-600/20'
                        : 'bg-white/5 text-gray-300 border border-white/5'
                    }`}
                  >
                    <span>{isAr ? link.nameAr : link.name}</span>
                    <ChevronRight
                      className={`h-5 w-5 ${isAr ? 'rotate-180' : ''}`}
                    />
                  </Link>
                ))}
              </nav>

              <div className='mt-auto space-y-4'>
                <button
                  onClick={handleLanguageChange}
                  className='w-full py-4 border border-white/10 rounded-2xl font-black text-white flex items-center justify-center gap-3 bg-white/5'
                >
                  <Globe className='h-5 w-5 text-medical-blue-400' />
                  {isAr ? 'Switch to English' : 'التغيير إلى العربية'}
                </button>
                <a
                  href='tel:+96550097296'
                  className='w-full py-4 bg-medical-blue-600 text-white rounded-2xl font-black flex items-center justify-center gap-3 shadow-xl shadow-medical-blue-500/20'
                >
                  <Phone size={20} />
                  {isAr ? 'اتصل بنا الآن' : 'Call Us Now'}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
